/** Staff Users (сотрудники) — создание аккаунтов + назначение групп прав.
 *
 * Раньше существовал только редактор групп прав (staff_groups.tsx) — сами
 * группы можно было создать, но не было UI чтобы завести сотрудника и
 * назначить его в группу. Backend API уже был готов (/admin/v1/users,
 * /admin/v1/staff/users/{id}/groups) — не хватало только этой страницы.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  KeyRound,
  Plus,
  Trash2,
  UserCog,
  Users as UsersIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import type { UserRole } from "@/lib/types";

interface StaffUser {
  id: number;
  email: string;
  full_name: string;
  role: UserRole;
  phone: string | null;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface Group {
  id: number;
  name: string;
  description: string | null;
  is_system: boolean;
}

const ROLE_LABELS: Record<UserRole, string> = {
  admin: "Admin",
  manager: "Manager",
  warehouse: "Warehouse",
  viewer: "Viewer",
};

export function StaffUsersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [groupsEditingId, setGroupsEditingId] = useState<number | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ["staff-users"],
    queryFn: async () => {
      const { data } = await api.get("/users");
      return data as StaffUser[];
    },
  });

  const { data: allGroups } = useQuery({
    queryKey: ["staff-groups"],
    queryFn: async () => {
      const { data } = await api.get("/staff/groups");
      return data as Group[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/users/${id}`);
    },
    onSuccess: () => {
      toast.success("Сотрудник удалён");
      queryClient.invalidateQueries({ queryKey: ["staff-users"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось удалить"),
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить сотрудника?",
    description: "Аккаунт будет деактивирован (soft-delete), вход станет недоступен.",
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (u: StaffUser) => {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      return { email: u.email, password: data.new_password as string };
    },
    onSuccess: (res) => setGeneratedPassword(res),
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось сбросить пароль"),
  });

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2">
            <UserCog className="w-6 h-6" />
            Сотрудники
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Аккаунты для входа в админку. Гранулярные права выдаются через{" "}
            <Link to="/configuration/staff-groups" className="underline">
              группы прав (RBAC)
            </Link>
            .
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Добавить сотрудника
        </Button>
      </div>

      {creating && (
        <UserForm
          mode="create"
          onClose={() => setCreating(false)}
          onCreated={(res) => setGeneratedPassword(res)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {users?.map((u) => (
          <Card key={u.id} className={!u.is_active ? "opacity-60" : undefined}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    {u.full_name}
                    <Badge variant="secondary" className="text-[10px]">
                      {ROLE_LABELS[u.role]}
                    </Badge>
                    {!u.is_active && (
                      <Badge variant="outline" className="text-[10px] text-destructive">
                        отключён
                      </Badge>
                    )}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">{u.email}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <UserGroupsBadges userId={u.id} />

              {editingId === u.id ? (
                <UserForm mode="edit" user={u} onClose={() => setEditingId(null)} />
              ) : groupsEditingId === u.id && allGroups ? (
                <GroupsAssignForm
                  userId={u.id}
                  allGroups={allGroups}
                  onClose={() => setGroupsEditingId(null)}
                />
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditingId(u.id)}>
                      Редактировать
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setGroupsEditingId(u.id)}
                    >
                      <UsersIcon className="w-3.5 h-3.5 mr-1" />
                      Группы
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resetPasswordMutation.mutate(u)}
                      disabled={resetPasswordMutation.isPending}
                    >
                      <KeyRound className="w-3.5 h-3.5 mr-1" />
                      Сбросить пароль
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => askDelete(u.id, u.full_name)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {users?.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          Сотрудников пока нет — добавьте первого.
        </p>
      )}

      {deleteDialog}

      {generatedPassword && (
        <GeneratedPasswordDialog
          email={generatedPassword.email}
          password={generatedPassword.password}
          onClose={() => setGeneratedPassword(null)}
        />
      )}
    </div>
  );
}

function UserGroupsBadges({ userId }: { userId: number }): JSX.Element | null {
  const { data: groups } = useQuery({
    queryKey: ["staff-user-groups", userId],
    queryFn: async () => {
      const { data } = await api.get(`/staff/users/${userId}/groups`);
      return data as Group[];
    },
  });

  if (!groups) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {groups.length === 0 ? (
        <span className="text-xs text-muted-foreground italic">
          Без группы прав — доступа к разделам не будет
        </span>
      ) : (
        groups.map((g) => (
          <Badge key={g.id} variant="outline" className="text-[10px]">
            {g.name}
          </Badge>
        ))
      )}
    </div>
  );
}

function UserForm({
  mode,
  user,
  onClose,
  onCreated,
}: {
  mode: "create" | "edit";
  user?: StaffUser;
  onClose: () => void;
  onCreated?: (res: { email: string; password: string }) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(user?.email ?? "");
  const [fullName, setFullName] = useState(user?.full_name ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [role, setRole] = useState<UserRole>(user?.role ?? "manager");
  const [isActive, setIsActive] = useState(user?.is_active ?? true);
  const [password, setPassword] = useState("");

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (mode === "create") {
        const { data } = await api.post("/users", {
          email: email.trim(),
          full_name: fullName.trim(),
          phone: phone.trim() || null,
          role,
          password: password.trim() || null,
        });
        return data;
      }
      if (user) {
        await api.patch(`/users/${user.id}`, {
          full_name: fullName.trim(),
          phone: phone.trim() || null,
          role,
          is_active: isActive,
        });
      }
      return null;
    },
    onSuccess: (data) => {
      toast.success(mode === "create" ? "Сотрудник создан" : "Сохранено");
      queryClient.invalidateQueries({ queryKey: ["staff-users"] });
      onClose();
      if (mode === "create" && data && onCreated) {
        onCreated({
          email: data.email,
          password: data.generated_password ?? password,
        });
      }
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка сохранения"),
  });

  return (
    <Card className={mode === "edit" ? "mt-3 border-primary/30" : undefined}>
      {mode === "create" && (
        <CardHeader>
          <CardTitle className="text-base">Новый сотрудник</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-4 pt-4">
        {mode === "create" && (
          <div className="space-y-1.5">
            <Label>Email *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ivanov@bestdigital.uz"
            />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Имя *</Label>
          <Input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Иван Иванов"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Телефон</Label>
          <Input
            value={phone ?? ""}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+998901234567"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Роль *</Label>
          <Select value={role} onValueChange={(v) => setRole(v as UserRole)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="warehouse">Warehouse</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Базовая роль (Admin — полный доступ ко всему). Детальные права по
            разделам выдаются группами после создания.
          </p>
        </div>
        {mode === "create" && (
          <div className="space-y-1.5">
            <Label>Пароль</Label>
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Оставьте пустым — сгенерируется автоматически"
            />
          </div>
        )}
        {mode === "edit" && (
          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>Активен</Label>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={
              (mode === "create" && (!email.trim() || !fullName.trim())) ||
              (mode === "edit" && !fullName.trim()) ||
              saveMutation.isPending
            }
          >
            {mode === "create" ? "Создать" : "Сохранить"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function GroupsAssignForm({
  userId,
  allGroups,
  onClose,
}: {
  userId: number;
  allGroups: Group[];
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { data: currentGroups, isLoading } = useQuery({
    queryKey: ["staff-user-groups", userId],
    queryFn: async () => {
      const { data } = await api.get(`/staff/users/${userId}/groups`);
      return data as Group[];
    },
  });
  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (currentGroups) setSelected(new Set(currentGroups.map((g) => g.id)));
  }, [currentGroups]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.put(`/staff/users/${userId}/groups`, {
        group_ids: Array.from(selected),
      });
    },
    onSuccess: () => {
      toast.success("Группы обновлены");
      queryClient.invalidateQueries({ queryKey: ["staff-user-groups", userId] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка сохранения"),
  });

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (isLoading) return <Skeleton className="h-24" />;

  return (
    <Card className="mt-3 border-primary/30">
      <CardContent className="space-y-3 pt-4">
        <Label className="block">Группы прав</Label>
        {allGroups.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Групп пока нет — сначала создайте их на странице «Группы прав».
          </p>
        ) : (
          <div className="border rounded-md divide-y">
            {allGroups.map((g) => (
              <label
                key={g.id}
                className="flex items-start gap-2 px-3 py-2 hover:bg-muted/50 cursor-pointer"
              >
                <Checkbox
                  checked={selected.has(g.id)}
                  onChange={() => toggle(g.id)}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm">{g.name}</div>
                  {g.description && (
                    <div className="text-xs text-muted-foreground">{g.description}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            Сохранить
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function GeneratedPasswordDialog({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}): JSX.Element {
  function copy() {
    navigator.clipboard.writeText(password);
    toast.success("Скопировано");
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Пароль сгенерирован</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Показывается только один раз — сохраните или передайте сотруднику сейчас.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Email</Label>
            <div className="text-sm font-mono">{email}</div>
          </div>
          <div>
            <Label className="text-xs">Пароль</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-muted px-2 py-1 text-sm font-mono break-all">
                {password}
              </code>
              <Button variant="outline" size="sm" onClick={copy}>
                Копировать
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Готово</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
